/**
 * Button component
 * @label : string, text before ":"
 * @value : string, text after ":" , ":" only if value is defined
 * type : string, css appearance "info", "action","action2", "selected", "warning", "success", "minWidth"
 * minWidth : bool, to give the min width to the btn
 * class="icon" if icon needed
 */
import { Component, EventEmitter, Input, Output } from '@angular/core';

export enum BtnStyleTypes {
    round = "round",
    roundO = "round-o",
    selected = "selected",
    info = "info",
    warning = "warning",
    headerBtn = "headerBtn",
    action = "action",
    actionRound = "actionRound",
    actionDo = "actionDo",
    actionPrincipal = "actionPrincipal",
    actionPrincipalRound = "actionPrincipalRound",
    actionBackRound = "actionBackRound",
    actionUndo = "actionUndo",
    actionMore = "actionMore",
    actionLink = "actionLink",
    actionHeaderLink = "actionHeaderLink",
    actionLinkUndo = "actionLinkUndo",
    actionInlineLink = "actionInlineLink",
    selectedAction = "selectedAction",
    action2 = "action2",
    actionHeader = "actionHeader",
    invisible = "invisible",
    inherit = "inherit",
    success = "success",
    hasText = "hasText",
    inactive = "inactive"
}

export enum BtnMinWidth {
    undefined = "",
    minWidth1 = "minWidth1",
    minWidth2 = "minWidth2",
    minWidthSmall = "minWidthSmall"
}

export enum Size {
    small = "small",
    normal = "normal",
    noSize = "noSize"
}

@Component({
    selector: 'btn',
    template: `
        <button [type]="btnType"
                [ngClass]="['btn', type, minWidth, classList, size]"
                [class.hasText]="label || value"
                [class.hoverPopInfo]="popInfo"
                [class.wtb-style]="wtbStyle"
                [class.is-full]="isFull"
                [disabled]="disableIf"
                [tabindex]="isAccessedWithTab ? 0 : -1"
                [ngStyle]="{'color': color}"
                (click)="onClick.emit($event)">
            <ng-content select=".icon"></ng-content>
            <label *ngIf="icon"><fa-icon *ngIf="(icon | iconNameToDefinition)" [icon]="icon | iconNameToDefinition"></fa-icon></label>

            <ng-container *ngIf="label">
                <ng-container *ngFor="let lab of toArray(label); let l = last; let i = index;">
                    <span [class.hidden]="l">,</span>
                    <ng-container *ngIf="i > 0">,</ng-container>
                    <span [innerHTML]="lab"></span></ng-container>
            </ng-container>

            <ng-container *ngIf="value">
                <ng-container *ngFor="let val of toArray(value); let l=last;">
                    <span [class.hidden]="l">,</span>
                    <span [innerHTML]="val"></span>
                </ng-container>
            </ng-container>

            <ng-content select=".icon-end"></ng-content>

            <div class="popInfoSmall" *ngIf="popInfo">
                {{popInfo}}
            </div>
        </button>
    `,
    styleUrls: ["./btn.component.scss"]
})
export class BtnComponent {
    @Input() public btnType: "button"|"input"|"submit" = "button";
    @Input() public label: string;
    @Input() public value: string;
    @Input() public type: BtnStyleTypes = BtnStyleTypes.info; //action, //selected
    @Input() public minWidth: BtnMinWidth = BtnMinWidth.undefined;
    @Input() public size: Size = Size.normal;
    @Input() public disableIf: boolean = false;
    @Input() public isAccessedWithTab: boolean = true;
    @Input() public popInfo: string;
    @Input() public classList: string = '';
    @Input() public wtbStyle: boolean = false;
    @Input() public icon: string = null;
    @Input() public color: string;
    @Input() public isFull: boolean;

    /**
     * Use this event if you have disabled buttons.
     * This event will not be fired if the button is disabled.
     */
    @Output() public readonly onClick: EventEmitter<MouseEvent> = new EventEmitter();

    public readonly btnStyleTypes: typeof BtnStyleTypes = BtnStyleTypes;

    public readonly roundTypes: BtnStyleTypes[] = [BtnStyleTypes.actionPrincipalRound, BtnStyleTypes.round, BtnStyleTypes.roundO];

    public toArray(value: string | string[]): string[] {
        if (!Array.isArray(value)) {
            return [value];
        }
        return value;
    }
}
